// AddViewSupportCertComponent.jsx  (อัปเดต: filter หน่วยงาน + Export Excel/PDF + map service 410 + DeptName dynamic)
import React, { useEffect, useMemo, useState } from "react";
import {
  Container, Row, Col, Label, Table, Badge
} from "reactstrap";
import Swal from "sweetalert2";
import getYear from "date-fns/getYear";
import getMonth from "date-fns/getMonth";
import DatePicker, { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import th from "date-fns/locale/th";
import { th as thBuddhist } from "date-fns/esm/locale";
import { addYears } from "date-fns/esm/fp";
import { format, parseISO } from "date-fns";

// ====== หน่วยงาน ======
import Select from "react-select";
import { GetVMdDept } from "../../share/api/MasterDataApi";

// ====== Export libs ======
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";

// ---------- Thai/Buddhist locale ----------
registerLocale("th-buddhist", {
  ...th,
  options: { ...th.options, weekStartsOn: 1 },
  formatLong: thBuddhist.formatLong,
});

// ---------- helpers ----------
const thaiMonths = ["มกราคม", "กุมภาพันธ์", "มีนาคม", "เมษายน", "พฤษภาคม", "มิถุนายน", "กรกฎาคม", "สิงหาคม", "กันยายน", "ตุลาคม", "พฤศจิกายน", "ธันวาคม"];
const range = (start, end) => new Array(end - start).fill().map((_, i) => i + start);
const years = range(1963, new Date().getFullYear() + 8);

const N = (v) => Number(String(v ?? 0).replace(/,/g, "")) || 0;
const fmtMoney = (n) => {
  const num = N(n);
  return num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

function formatMonthYearBE(date) {
  if (!date) return "";
  const be = addYears(543, date);
  return format(be, "MMMM yyyy", { locale: th });
}
function toThaiDateLabel(iso) {
  if (!iso) return "-";
  if (typeof iso === "string" && /\s[ก-๙]/.test(iso)) return iso;
  const d = typeof iso === "string" ? parseISO(iso) : iso;
  if (!(d instanceof Date) || isNaN(d)) return "-";
  const be = addYears(543, d);
  return `${format(be, "dd")} ${format(be, "MMMM", { locale: th })} ${format(be, "yyyy")}`;
}

// ====== PDF Thai font helpers ======
function base64FromArrayBuffer(buf) {
  const b = new Uint8Array(buf);
  const c = 0x8000;
  let s = "";
  for (let i = 0; i < b.length; i += c) s += String.fromCharCode.apply(null, b.subarray(i, i + c));
  return btoa(s);
}
let thaiFontLoaded = false;
async function ensureThaiFontLoaded() {
  if (thaiFontLoaded) return;
  const [regRes, boldRes] = await Promise.all([
    fetch("/fonts/Sarabun-Regular.ttf"),
    fetch("/fonts/Sarabun-Bold.ttf"),
  ]);
  const [regBuf, boldBuf] = await Promise.all([regRes.arrayBuffer(), boldRes.arrayBuffer()]);
  const regB64 = base64FromArrayBuffer(regBuf);
  const boldB64 = base64FromArrayBuffer(boldBuf);
  jsPDF.API.events.push(["addFonts", function () {
    this.addFileToVFS("Sarabun-Regular.ttf", regB64);
    this.addFileToVFS("Sarabun-Bold.ttf", boldB64);
    this.addFont("Sarabun-Regular.ttf", "Sarabun", "normal");
    this.addFont("Sarabun-Bold.ttf", "Sarabun", "bold");
  }]);
  new jsPDF();
  thaiFontLoaded = true;
}

// ===============================================================
//                        MAIN COMPONENT
// ===============================================================
export function OperationReport410Component() {
  const [reportMonth, setReportMonth] = useState("");     // "YYYY-MM"
  const [reportMonthDate, setReportMonthDate] = useState(null);
  const user = JSON.parse(localStorage.getItem("userData") || "{}");
  const roleId = String(user?.roleId ?? user?.roldId ?? "");
  const deptId = user?.deptId || "";

  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [isEditingReport, setIsEditingReport] = useState(false);

  // ====== หน่วยงาน (มี all) ======
  const [deptsData, setDeptsData] = useState([]); // [{value,label}]
  const [deptIdFilter, setDeptIdFilter] = useState("all");

  // แสดงคอลัมน์หน่วยงาน เฉพาะสิทธิ์กรม + เลือกทุกหน่วยงาน
  const showDeptCol = (String(user?.roldId ?? roleId) === "4" && String(deptIdFilter || "all") === "all");

  // master: สาขาอาชีพ (เพื่อแสดงชื่อ)
  const [lawOcuData, setLawOcuData] = useState([]);
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch("/api/Borrow/getLawOcuData");
        const j = await res.json();
        setLawOcuData(j?.dataenum || []);
      } catch { }
    })();
  }, []);
  const getLawOccName = (id) =>
    lawOcuData.find(x => String(x.lawOccupationId) === String(id))?.lawOccupationName || "-";

  // โหลดรายการหน่วยงานครั้งแรก
  useEffect(() => {
    GetVMdDept({ setDeptsData, setLoading });
  }, []);

  // sync string <-> Date
  useEffect(() => {
    if (!reportMonth) { setReportMonthDate(null); return; }
    const [y, m] = reportMonth.split("-").map(Number);
    setReportMonthDate(new Date(y, m - 1, 1));
  }, [reportMonth]);

  // helper: แปลง deptId -> label
  const getDeptLabelById = (id) => {
    const opt = [{ value: "all", label: "ทุกหน่วยงาน" }, ...(deptsData || [])]
      .find(o => String(o.value) === String(id));
    return opt?.label || "-";
  };

  // Load month (map -> service ListSupportOperation410)
  const checkAndLoadMonth = async () => {
    try {
      setLoading(true);
      const fd = new FormData();
      fd.append("monthYear", reportMonth === "" ? "all" : reportMonth);  // YYYY-MM หรือ all
      fd.append("deptIdFilter", deptIdFilter || "all");                  // all หรือ dept เฉพาะ

      // NOTE: ปรับ path ให้ตรง Controller ของคุณ เช่น /api/SupportOperation/listSupportOperation410
      const res = await fetch("/api/SupportOperation/listSupportOperation410", {
        method: "POST",
        headers: { token: user?.token || "" },
        body: fd,
      });
      const j = await res.json();
      if (j?.status !== 1) throw new Error(j?.message || "โหลดข้อมูลไม่ได้");

      // map service -> rows
      const rows = (j?.dataenum || []).map((s, i) => ({
        id: s.supportOperationCertId ?? i + 1,
        deptId: s.deptId,
        deptNameNormalized: getDeptLabelById(s.deptId),     // <<<< เพิ่มชื่อหน่วยงาน
        lawOccupationId: s.lawOccupationId,
        courseName: s.courseName || "-",
        testingPersonAmount: N(s.testingPersonAmount),
        passPersonAmount: N(s.passPersonAmount),
        tutorAmountExpense: N(s.tutorAmountExpense),
        lunchAmountExpense: N(s.lunchAmountExpense),
        bakeAmountExpense: N(s.bakeAmountExpense),
        utilityAmountExpense: N(s.utilityAmountExpense),
        paymentDate: s.paymentDate, // yyyy-MM-dd หรือ null
        statusApprove: s.statusApprove,
        officerCreateBy: s.officerCreateBy
      }));

      setItems(rows);
      setIsEditingReport(rows.length > 0);
    } catch (e) {
      console.error(e);
      Swal.fire("โหลดข้อมูลเดือนไม่สำเร็จ", e?.message || "", "error");
      setItems([]);
      setIsEditingReport(false);
    } finally {
      setLoading(false);
    }
  };

  // summary
  const summary = useMemo(() => {
    const tutor = items.reduce((a, r) => a + N(r.tutorAmountExpense), 0);
    const lunch = items.reduce((a, r) => a + N(r.lunchAmountExpense), 0);
    const bake = items.reduce((a, r) => a + N(r.bakeAmountExpense), 0);
    const util = items.reduce((a, r) => a + N(r.utilityAmountExpense), 0);
    const total = tutor + lunch + bake + util;
    return { tutor, lunch, bake, util, total };
  }, [items]);

  // ====== Build Excel (dynamic Dept col) ======
  const buildAoaAndMerges = (rows) => {
    const headRow1 = [
      "ลำดับ",
      "สาขาอาชีพ",
      "หลักสูตร",
      ...(showDeptCol ? ["หน่วยงาน"] : []),
      "จำนวนผู้เข้ารับการฝึกอบรมฯ (คน)",
      "จำนวนผู้ผ่านการฝึกอบรมฯ (คน)",
      "จำนวนเงินช่วยเหลือหรืออุดหนุน", "", "", "", // 4 ย่อย
      "รวม (บาท)",
      "วันที่จ่ายเงินช่วยเหลือหรืออุดหนุน",
    ];
    const headRow2 = [
      "", "", ...(showDeptCol ? [""] : []), "", "",
      "ค่าตอบแทนวิทยากร (บาท)",
      "ค่าอาหารกลางวัน (บาท)",
      "ค่าอาหารว่าง และเครื่องดื่ม (บาท)",
      "ค่าวัสดุ (บาท)",
      "",
      "",
    ];

    const aoa = [headRow1, headRow2];

    const merges = [];
    // ต้นคอลัมน์ rowSpan 2 (index 0..(fixed-1))
    const fixed = showDeptCol ? 6 : 5;
    // mapping index: 0 ลำดับ, 1 สาขา, 2 หลักสูตร, [3 หน่วยงาน], 3/4 เป็น counts ถ้าไม่มี dept; เพื่อให้ง่าย คำนวณ explicit:
    // เราจะวางลำดับ: 0, สาขา:1, หลักสูตร:2, หน่วยงาน:3*(ถ้ามี), เข้าฝึก: baseCount, ผ่าน: baseCount+1, group money เริ่ม baseMoney
    // คำนวณตำแหน่งจาก fixed ด้านล่าง

    // rowSpan for leading columns
    // ลำดับ, สาขา, หลักสูตร
    merges.push({ s: { r: 0, c: 0 }, e: { r: 1, c: 0 } });
    merges.push({ s: { r: 0, c: 1 }, e: { r: 1, c: 1 } });
    merges.push({ s: { r: 0, c: 2 }, e: { r: 1, c: 2 } });
    let colPtr = 3;
    if (showDeptCol) {
      merges.push({ s: { r: 0, c: 3 }, e: { r: 1, c: 3 } });
      colPtr = 4;
    }
    // เข้าฝึก / ผ่าน
    merges.push({ s: { r: 0, c: colPtr + 0 }, e: { r: 1, c: colPtr + 0 } });
    merges.push({ s: { r: 0, c: colPtr + 1 }, e: { r: 1, c: colPtr + 1 } });
    // กลุ่มเงิน 4 คอลัมน์
    const moneyStart = colPtr + 2;
    merges.push({ s: { r: 0, c: moneyStart }, e: { r: 0, c: moneyStart + 3 } });
    // รวม / วันที่จ่าย
    merges.push({ s: { r: 0, c: moneyStart + 4 }, e: { r: 1, c: moneyStart + 4 } });
    merges.push({ s: { r: 0, c: moneyStart + 5 }, e: { r: 1, c: moneyStart + 5 } });

    // rows
    rows.forEach((r, idx) => {
      const total = N(r.tutorAmountExpense) + N(r.lunchAmountExpense) + N(r.bakeAmountExpense) + N(r.utilityAmountExpense);
      const row = [];
      row[0] = idx + 1;
      row[1] = getLawOccName(r.lawOccupationId);
      row[2] = r.courseName || "-";
      let p = 3;
      if (showDeptCol) { row[p++] = r.deptNameNormalized || "-"; }
      row[p++] = N(r.testingPersonAmount);
      row[p++] = N(r.passPersonAmount);
      row[p++] = N(r.tutorAmountExpense);
      row[p++] = N(r.lunchAmountExpense);
      row[p++] = N(r.bakeAmountExpense);
      row[p++] = N(r.utilityAmountExpense);
      row[p++] = N(total);
      row[p++] = r.paymentDate || "-";
      aoa.push(row);
    });

    return { aoa, merges, fixedCols: showDeptCol ? 4 : 3, moneyStart };
  };

  const handleExportExcel = () => {
    if (!items.length) return Swal.fire("ไม่มีข้อมูลสำหรับส่งออก", "", "warning");
    const { aoa, merges, fixedCols, moneyStart } = buildAoaAndMerges(items);
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws["!merges"] = merges;

    // set widths
    const widths = [
      { wch: 8 },   // ลำดับ
      { wch: 28 },  // สาขาอาชีพ
      { wch: 28 },  // หลักสูตร
    ];
    if (showDeptCol) widths.push({ wch: 28 }); // หน่วยงาน
    widths.push(
      { wch: 26 },  // เข้าฝึก
      { wch: 26 },  // ผ่าน
      { wch: 26 },  // วิทยากร
      { wch: 22 },  // กลางวัน
      { wch: 30 },  // ว่าง/เครื่องดื่ม
      { wch: 16 },  // วัสดุ
      { wch: 16 },  // รวม
      { wch: 26 }   // วันที่จ่าย
    );
    ws["!cols"] = widths;

    // summary row
    const r = aoa.length;
    const totalTest = items.reduce((s, x) => s + N(x.testingPersonAmount), 0);
    const totalPass = items.reduce((s, x) => s + N(x.passPersonAmount), 0);
    const totalTutor = items.reduce((s, x) => s + N(x.tutorAmountExpense), 0);
    const totalLunch = items.reduce((s, x) => s + N(x.lunchAmountExpense), 0);
    const totalBake = items.reduce((s, x) => s + N(x.bakeAmountExpense), 0);
    const totalUtil = items.reduce((s, x) => s + N(x.utilityAmountExpense), 0);
    const totalAll = totalTutor + totalLunch + totalBake + totalUtil;

    // "รวม" ครอบตั้งแต่คอลัมน์แรกจนถึงก่อน "ค่าตอบแทนวิทยากร"
    ws[XLSX.utils.encode_cell({ r, c: 0 })] = { t: "s", v: "รวม" };
    const mergeEnd = (showDeptCol ? 3 : 2);
    ws["!merges"].push({ s: { r, c: 0 }, e: { r, c: mergeEnd } });

    // เข้าฝึก/ผ่าน
    const baseCounts = (showDeptCol ? 4 : 3);
    ws[XLSX.utils.encode_cell({ r, c: baseCounts + 0 })] = { t: "n", v: totalTest };
    ws[XLSX.utils.encode_cell({ r, c: baseCounts + 1 })] = { t: "n", v: totalPass };
    // 4 เงิน
    const baseMoney = baseCounts + 2;
    ws[XLSX.utils.encode_cell({ r, c: baseMoney + 0 })] = { t: "n", v: totalTutor };
    ws[XLSX.utils.encode_cell({ r, c: baseMoney + 1 })] = { t: "n", v: totalLunch };
    ws[XLSX.utils.encode_cell({ r, c: baseMoney + 2 })] = { t: "n", v: totalBake };
    ws[XLSX.utils.encode_cell({ r, c: baseMoney + 3 })] = { t: "n", v: totalUtil };
    // รวมเงินทั้งหมด
    ws[XLSX.utils.encode_cell({ r, c: baseMoney + 4 })] = { t: "n", v: totalAll };

    XLSX.utils.book_append_sheet(wb, ws, "รายงาน ชอ.4-10");
    const fileName = `แบบรายงานผลการพิจารณาอนุมัติการให้เงินช่วยเหลือหรืออุดหนุนจากกองทุนพัฒนาฝีมือแรงงาน ชอ4-10.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  // ====== Export PDF (dynamic Dept col) ======
  const handleExportPDF = async () => {
    if (!items.length) return Swal.fire("ไม่มีข้อมูลสำหรับส่งออก", "", "warning");
    await ensureThaiFontLoaded();

    const titleMonth = formatMonthYearBE(reportMonthDate) || "ทุกเดือน";
    const deptLabel =
      ([{ value: "all", label: "ทุกหน่วยงาน" }, ...(deptsData || [])]
        .find(d => String(d.value) === String(deptIdFilter))?.label) || "ทุกหน่วยงาน";

    const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a3" });
    doc.setFont("Sarabun", "bold"); doc.setFontSize(16);
    doc.text(`แบบรายงานผลการพิจารณาอนุมัติการให้เงินช่วยเหลือหรืออุดหนุนจากกองทุนพัฒนาฝีมือแรงงาน ชอ.4-10`, 40, 40);

    const headTop = [
      { content: "ลำดับ", rowSpan: 2 },
      { content: "สาขาอาชีพ", rowSpan: 2 },
      { content: "หลักสูตร", rowSpan: 2 },
      ...(showDeptCol ? [{ content: "หน่วยงาน", rowSpan: 2 }] : []),
      { content: "จำนวนผู้เข้ารับการฝึกอบรมฯ (คน)", rowSpan: 2 },
      { content: "จำนวนผู้ผ่านการฝึกอบรมฯ (คน)", rowSpan: 2 },
      { content: "จำนวนเงินช่วยเหลือหรืออุดหนุน", colSpan: 4 },
      { content: "รวม (บาท)", rowSpan: 2 },
      { content: "วันที่จ่ายเงินช่วยเหลือหรืออุดหนุน", rowSpan: 2 },
    ];
    const headSub = [
      { content: "ค่าตอบแทนวิทยากร (บาท)" },
      { content: "ค่าอาหารกลางวัน (บาท)" },
      { content: "ค่าอาหารว่าง และเครื่องดื่ม (บาท)" },
      { content: "ค่าวัสดุ (บาท)" },
    ];
    const head = [headTop, headSub];

    const body = items.map((r, idx) => {
      const total = N(r.tutorAmountExpense) + N(r.lunchAmountExpense) + N(r.bakeAmountExpense) + N(r.utilityAmountExpense);
      const row = [
        idx + 1,
        getLawOccName(r.lawOccupationId),
        r.courseName || "-",
      ];
      if (showDeptCol) row.push(r.deptNameNormalized || "-");
      row.push(
        fmtMoney(r.testingPersonAmount),
        fmtMoney(r.passPersonAmount),
        fmtMoney(r.tutorAmountExpense),
        fmtMoney(r.lunchAmountExpense),
        fmtMoney(r.bakeAmountExpense),
        fmtMoney(r.utilityAmountExpense),
        fmtMoney(total),
        toThaiDateLabel(r.paymentDate),
      );
      return row;
    });

    doc.setFont("Sarabun", "normal");
    doc.autoTable({
      head,
      body,
      startY: 60,
      styles: { font: "Sarabun", fontSize: 10, cellPadding: 4, halign: "center", overflow: "linebreak" },
      headStyles: { fillColor: [239, 243, 255], textColor: [0, 0, 0], font: "Sarabun", fontStyle: "bold" },
      columnStyles: {
        1: { halign: "left" }, // สาขาอาชีพ
        2: { halign: "left" }, // หลักสูตร
        ...(showDeptCol ? { 3: { halign: "left" } } : {}),
        ...(showDeptCol ? { 6: { halign: "right" }, 7: { halign: "right" }, 8: { halign: "right" }, 9: { halign: "right" }, 10: { halign: "right" }, 11: { halign: "center" } }
          : { 5: { halign: "right" }, 6: { halign: "right" }, 7: { halign: "right" }, 8: { halign: "right" }, 9: { halign: "right" }, 10: { halign: "center" } }),
      },
      didDrawPage: (data) => {
        const n = doc.internal.getNumberOfPages();
        doc.setFont("Sarabun", "normal"); doc.setFontSize(9);
        doc.text(`หน้า ${data.pageNumber} / ${n}`, data.settings.margin.left, doc.internal.pageSize.getHeight() - 10);
      },
    });

    // summary
    const totalTest = items.reduce((s, x) => s + N(x.testingPersonAmount), 0);
    const totalPass = items.reduce((s, x) => s + N(x.passPersonAmount), 0);
    const totalTutor = items.reduce((s, x) => s + N(x.tutorAmountExpense), 0);
    const totalLunch = items.reduce((s, x) => s + N(x.lunchAmountExpense), 0);
    const totalBake = items.reduce((s, x) => s + N(x.bakeAmountExpense), 0);
    const totalUtil = items.reduce((s, x) => s + N(x.utilityAmountExpense), 0);
    const totalAll = totalTutor + totalLunch + totalBake + totalUtil;

    const y = (doc.lastAutoTable?.finalY || 60) + 24;
    doc.setFont("Sarabun", "bold"); doc.setFontSize(12);
    doc.text(
      `รวม: เข้าฝึกอบรม ${fmtMoney(totalTest)} คน | ผ่าน ${fmtMoney(totalPass)} คน | ` +
      `วิทยากร ${fmtMoney(totalTutor)} | กลางวัน ${fmtMoney(totalLunch)} | ว่าง/เครื่องดื่ม ${fmtMoney(totalBake)} | วัสดุ ${fmtMoney(totalUtil)} | รวมเงิน ${fmtMoney(totalAll)} บาท`,
      40, y
    );

    const fileName = `แบบรายงานผลการพิจารณาอนุมัติการให้เงินช่วยเหลือหรืออุดหนุนจากกองทุนพัฒนาฝีมือแรงงาน ชอ4-10.pdf`;
    doc.save(fileName);
  };

  return (
    <Container className="py-3">
      {/* Header controls */}
      <Row className="g-3 align-items-end">
        {/* Filter หน่วยงาน */}
        <Col md={6}>
          <Label>ชื่อหน่วยงาน</Label>
          <Select
            isSearchable
            styles={{
              control: (b) => ({ ...b, borderRadius: "20px" }),
              singleValue: (b) => ({ ...b, color: "#0A66C2" }),
            }}
            options={[{ value: "all", label: "ทุกหน่วยงาน" }, ...(deptsData || [])]}
            value={
              [{ value: "all", label: "ทุกหน่วยงาน" }, ...(deptsData || [])]
                .find((o) => String(o.value) === String(deptIdFilter)) || null
            }
            onChange={(opt) => setDeptIdFilter(opt?.value ?? "all")}
            placeholder={<div className="input-color text-center">กรุณาเลือกหน่วยงาน</div>}
            className="rounded-pill text-center input-color"
          />
        </Col>

        <Col md={6}>
          <Label>รอบรายงาน (เดือน/ปี)</Label>
          <DatePicker
            selected={reportMonthDate}
            onChange={(date) => {
              if (date instanceof Date && !isNaN(date)) {
                const y = date.getFullYear();
                const m = String(date.getMonth() + 1).padStart(2, "0");
                setReportMonth(`${y}-${m}`);
                setReportMonthDate(new Date(y, Number(m) - 1, 1));
              } else {
                setReportMonth(""); setReportMonthDate(null);
              }
            }}
            dateFormat="MMMM yyyy"
            showMonthYearPicker
            showFullMonthYearPicker
            locale="th-buddhist"
            placeholderText="เลือกเดือน/ปี (พ.ศ.)"
            value={formatMonthYearBE(reportMonthDate)}
            renderCustomHeader={({ date, changeYear, changeMonth, decreaseMonth, increaseMonth, prevMonthButtonDisabled, nextMonthButtonDisabled }) => (
              <div className="d-flex justify-content-between align-items-center px-2 py-1" style={{ gap: 8 }}>
                <button type="button" className="btn btn-sm btn-light" onClick={decreaseMonth} disabled={prevMonthButtonDisabled}>‹</button>
                <div className="d-flex align-items-center" style={{ gap: 8 }}>
                  <select className="form-select form-select-sm" style={{ width: 140 }}
                    value={thaiMonths[getMonth(date)]}
                    onChange={({ target: { value } }) => changeMonth(thaiMonths.indexOf(value))}
                  >
                    {thaiMonths.map(m => <option key={m} value={m}>{m}</option>)}
                  </select>
                  <select className="form-select form-select-sm" style={{ width: 110 }}
                    value={getYear(date)}
                    onChange={({ target: { value } }) => changeYear(Number(value))}
                  >
                    {years.map(y => <option key={y} value={y}>{y + 543}</option>)}
                  </select>
                </div>
                <button type="button" className="btn btn-sm btn-light" onClick={increaseMonth} disabled={nextMonthButtonDisabled}>›</button>
              </div>
            )}
            customInput={
              <input className="form-control rounded-pill text-center input-color" readOnly
                value={formatMonthYearBE(reportMonthDate)} placeholder="เลือกเดือน/ปี (พ.ศ.)" />
            }
          />
        </Col>

        <Col md="auto" className="pt-4 d-flex gap-2">
          <button className="btn btn-primary" onClick={checkAndLoadMonth} >
            ตรวจสอบ/โหลดรายงาน
          </button>
          <button className="btn btn-outline-success" onClick={handleExportExcel} disabled={!items.length}>
            ส่งออก Excel
          </button>
          <button className="btn btn-outline-danger" onClick={handleExportPDF} disabled={!items.length}>
            ส่งออก PDF
          </button>
        </Col>

        <Col md="auto" className="pt-4">
          {reportMonth && (
            <Badge color={isEditingReport ? "warning" : "success"} pill className="px-3 py-2">
              {isEditingReport ? "โหมดแก้ไขรายงาน" : "โหมดสร้างรายงานใหม่"}
            </Badge>
          )}
        </Col>
      </Row>

      {/* Table */}
      <div className="mt-3">
        <Table bordered responsive>
          <thead className="table-light">
            <tr>
              <th rowSpan={2} className="text-center align-middle">ที่</th>
              <th rowSpan={2} className="text-center align-middle">สาขาอาชีพ</th>
              <th rowSpan={2} className="text-center align-middle">หลักสูตร</th>
              {showDeptCol && (
                <th rowSpan={2} className="text-center align-middle">หน่วยงาน</th>
              )}
              <th rowSpan={2} className="text-center align-middle">จำนวนผู้เข้ารับการฝึกอบรมฯ<br />(คน)</th>
              <th rowSpan={2} className="text-center align-middle">จำนวนผู้ผ่านการฝึกอบรมฯ<br />(คน)</th>
              <th colSpan={4} className="text-center align-middle">จำนวนเงินช่วยเหลือหรืออุดหนุน</th>
              <th rowSpan={2} className="text-center align-middle">รวม<br />(บาท)</th>
              <th rowSpan={2} className="text-center align-middle">วันที่จ่ายเงินช่วยเหลือหรืออุดหนุน</th>
            </tr>
            <tr>
              <th className="text-center align-middle">ค่าตอบแทนวิทยากร<br />(บาท)</th>
              <th className="text-center align-middle">ค่าอาหารกลางวัน<br />(บาท)</th>
              <th className="text-center align-middle">ค่าอาหารว่าง และเครื่องดื่ม<br />(บาท)</th>
              <th className="text-center align-middle">ค่าวัสดุ<br />(บาท)</th>
            </tr>
          </thead>

          <tbody>
            {items.length === 0 && (
              <tr><td colSpan={showDeptCol ? 12 : 11} className="text-center">ยังไม่มีรายการ</td></tr>
            )}

            {items.map((r, idx) => {
              const total = N(r.tutorAmountExpense) + N(r.lunchAmountExpense) + N(r.bakeAmountExpense) + N(r.utilityAmountExpense);
              return (
                <tr key={r.id || idx}>
                  <td className="text-center">{idx + 1}</td>
                  <td className="text-center">{getLawOccName(r.lawOccupationId)}</td>
                  <td className="text-start">{r.courseName || "-"}</td>
                  {showDeptCol && <td className="text-start">{r.deptNameNormalized || "-"}</td>}
                  <td className="text-center">{fmtMoney(r.testingPersonAmount)}</td>
                  <td className="text-center">{fmtMoney(r.passPersonAmount)}</td>
                  <td className="text-center">{fmtMoney(r.tutorAmountExpense)}</td>
                  <td className="text-center">{fmtMoney(r.lunchAmountExpense)}</td>
                  <td className="text-center">{fmtMoney(r.bakeAmountExpense)}</td>
                  <td className="text-center">{fmtMoney(r.utilityAmountExpense)}</td>
                  <td className="text-center">{fmtMoney(total)}</td>
                  <td className="text-center">{toThaiDateLabel(r.paymentDate)}</td>
                </tr>
              );
            })}
          </tbody>

          {items.length > 0 && (
            <tfoot>
              <tr>
                <td colSpan={showDeptCol ? 6 : 5} className="text-end fw-bold">รวม</td>
                <td className="text-center fw-bold">{fmtMoney(summary.tutor)}</td>
                <td className="text-center fw-bold">{fmtMoney(summary.lunch)}</td>
                <td className="text-center fw-bold">{fmtMoney(summary.bake)}</td>
                <td className="text-center fw-bold">{fmtMoney(summary.util)}</td>
                <td className="text-center fw-bold">{fmtMoney(summary.total)}</td>
                <td></td>
              </tr>
            </tfoot>
          )}
        </Table>
      </div>

      {loading && (
        <div className="position-fixed bottom-0 end-0 p-3">
          <span className="badge bg-secondary">กำลังโหลด...</span>
        </div>
      )}
    </Container>
  );
}
